<script setup>
import {computed, inject, onMounted, ref} from "vue";
import useComment, {USE_COMMENT_KEY} from "./CommentComposition";
import useHeightTransition from "./HeightTransitionComposition";
import ChildList from "./ChildList.vue";
import CommentContainer from "./CommentContainer.vue";
import ConfirmModal from "./ConfirmModal.vue";

const siteUrl = document.querySelector('meta[name="site-url"]').content;

//コメントエリア識別用のユニークな ID
const COMMENT_ID = 'js-comment-'+Math.random().toString(36).slice(-8);

const props = defineProps({
  index: {
    type: Number,
    required: true
  },
  comment: {
    type: Object,
    required: true
  },
  thread: {
    type: Array,
    required: true
  },
  commentEmpathy: {
    type: Array,
    required: true
  },
  commentViolations: {
    type: Array,
    required: true
  },
});

const emits = defineEmits(['delete-comment', 'open-thread']);

//コンポジションを定義、配信
const composition = inject(USE_COMMENT_KEY, useComment());
const heightTransition = useHeightTransition();

//CommentContainer を描画する準備フラグ
//CommentContainer は内部で teleport を使っているが、これは指定先の DOM がすでに描画済の場合のみ使用できる
//teleport 先は当コンポーネントが描画するので、このコンポーネントの onMounted を待つ必要があるため、タイミングをこの ref で調節する
const commentReady = ref(false);

//共感しているかどうか初期値
const empathy = ref(props.commentEmpathy.some(commentId => Number(commentId) === Number(props.comment.id)));

//共感を付け替えした際、共感数を増減させるための調整値
const empathyFluctuation = ref(0);

//違反報告しているかどうか初期値
const violation = ref(props.commentViolations.some(commentId => Number(commentId) === Number(props.comment.id)));

//0: モーダルを表示しない
//1: 違反報告モーダルを表示する
//2: 取り消しモーダルを表示する
const violationModalMode = ref(0);

/**
 * 誕生日より投稿者の現年齢を返す
 * @type {ComputedRef<unknown>}
 */
const contributorAge = computed(() => {
  //正規表現により生年月日が YYYY-mm-dd 形式か判定し、定型でなければ 631119600000 (1990-01-01 00:00:00) を基準に仮計算する
  const birthdayTimestamp = /^-?\d+$/.test(props.comment.birthday)
      ? Number(props.comment.birthday) * 1000
      : 631119600000;

  //誕生日文字列を得るための Date オブジェクトを得る
  const birthday = new Date(birthdayTimestamp);

  //本日の日付を得る
  const today = new Date();

  //引き算して返す
  return Math.floor((
      Number(today.getFullYear().toString().padStart(4, '0')+(today.getMonth() + 1).toString().padStart(2, '0')+today.getDate().toString().padStart(2, '0')) -
      Number(birthday.getFullYear().toString().padStart(4, '0')+(birthday.getMonth() + 1).toString().padStart(2, '0')+birthday.getDate().toString().padStart(2, '0'))
  ) / 10000);
});

/**
 * datetime 属性向けの年月日時分文字列を返す
 * @type {ComputedRef<string>}
 */
const createdAtForAttribute = computed(() => {
  //タイムスタンプから Date オブジェクトを生成
  const date = new Date(Number(props.comment.created_at) * 1000);

  //結合して返す
  return date.getFullYear().toString().padStart(4, '0')
      +'-'
      +(date.getMonth() + 1).toString().padStart(2, '0')
      +'-'
      +date.getDate().toString().padStart(2, '0')
      +'T'
      +date.getHours().toString().padStart(2, '0')
      +':'
      +date.getMinutes().toString().padStart(2, '0');
});

/**
 * 人間向けの年月日時分文字列を返す
 * @type {ComputedRef<string>}
 */
const createdAtForHuman = computed(() => createdAtForAttribute.value.replace('T', ' ').replace(/-/g, '.'));

//共感数
const empathyCount = computed(() => Number(props.comment.count_empathy) + empathyFluctuation.value);

//コメントの改行
const contentParagraph = computed(() => props.comment.content.replace(/\r\n/g, "\n").split("\n"));

//返信可能かどうか
const isReply = computed(() => {
  //まずは以下の条件に一切引っかからないか判定
  //・ログインしていない
  //・コメント 0 のスレッド
  //・スレッドの末尾ではない
  if (
      Number(composition.state.userId) === 0 ||
      props.thread.length < 1 ||
      props.index !== (props.thread.length - 1)
  ) {
    return false;
  }

  //投稿者以外の場合は、スレッド先頭のコメントが自分のもので無かったら不可
  if ( ! composition.state.isMyPost && Number(props.thread[0].is_self) === 0) {
    return false;
  }

  //返信先が自身のコメントかつ、削除されていない場合は不可
  if (Number(props.comment.is_self) !== 0 && props.comment.deleted_at === null) {
    return false;
  }

  //返信先が自身のコメントではないかつ、削除済の場合は不可
  if (Number(props.comment.is_self) === 0 && props.comment.deleted_at !== null) {
    return false;
  }

  //コメント可
  return true;
});

//返信をする際に使用するコメント ID
const replyCommentId = computed(() => {
  //投稿が自身のものであるのであれば、その投稿時に使用した ID を返す
  if (composition.state.isMyPost) {
    return Number(composition.state.postUserNicknameId);
  }

  //そうでない場合は、スレッドの先頭のコメントに使用した ID を返す
  return props.thread.length > 0 ? Number(props.thread[0].user_nickname_id) : 0;
});

//共感のトグル
const toggleEmpathy = async (event) => {
  //不正な DOM を弾く
  if (
      ! (event.target instanceof HTMLInputElement) ||
      event.target.type !== 'checkbox'
  ) {
    return;
  }

  //更新を試みる
  const result = await composition.toggleCommentEmpathy(event.target.checked, Number(props.comment.id));

  //成功したら情報を同期する
  if (result) {
    //ローカルの値を同期する
    empathy.value = props.commentEmpathy.some(commentId => Number(commentId) === Number(props.comment.id));

    //共感数も同期する
    empathyFluctuation.value = empathyFluctuation.value + (empathy.value ? 1 : -1);
    empathyFluctuation.value = empathyFluctuation.value > 1 ? 1 : empathyFluctuation.value;
    empathyFluctuation.value = empathyFluctuation.value < -1 ? -1 : empathyFluctuation.value;
  }

  //結果が失敗ならもとに戻す
  else {
    empathy.value = props.commentEmpathy.some(commentId => Number(commentId) === Number(props.comment.id));
  }
};

//違反報告のトグル
const toggleViolation = async (checked) => {
  //更新を試みる
  const result = await composition.toggleCommentViolation(checked, Number(props.comment.id));

  //ローカルの値を同期する
  violation.value = props.commentViolations.some(commentId => Number(commentId) === Number(props.comment.id));

  //結果が成功ならモーダルを表示
  if (result) {
    //対象のモーダルを開く
    violationModalMode.value = checked ? 1 : 2;

    //#modal を確保
    const modal = document.getElementById('modal');

    //#modal を開く
    if (modal) {
      modal.classList.add('isDsply');
    }
  }
};

//スレッドを開く
const openThread = () => {
  emits('open-thread');
};

//コメント描画の準備を行う
onMounted(() => {
  commentReady.value = true;

  setTimeout(() => {
    empathy.value = props.commentEmpathy.some(commentId => Number(commentId) === Number(props.comment.id));
  }, 3000);
});
</script>

<template>
  <div>
    <div class="pageBoardWrapper">
      <div class="lyPd8 pBorderBottom1pxSolidGray4">
        <div class="lyHorizontalBw">
          <div class="lyVerticalCntBreak lyMr16">
            <p class="lyMr4">
          <span
            v-if="comment.user_link_id === ''"
            class="pFontBold"
            style="pointer-events:none;"
          >{{comment.user_nickname}}</span>
              <a
                v-else
                :href="siteUrl+'place/user/'+comment.user_link_id"
                class="pFontBold"
              >{{comment.user_nickname}}</a>
            </p>
            <div class="lyHorizontal pFont12 pFontMedium">
              <span class="lyMr4">さん</span>
              <span
                v-if="comment.public_prefecture !== ''"
                class="lyMr4"
              >{{comment.public_prefecture}}</span>
              <span>{{Math.floor(contributorAge / 10) * 10}}代</span>
            </div>
          </div>
        </div>
        <ul
          v-if="comment.public_children.length > 0"
          class="lyHorizontalBreak lyMt4"
        >
          <ChildList
            v-for="child in comment.public_children"
            :key="child.id"
            :child="child"
          />
        </ul>
      </div>
      <div class="lyPd8 lyVerticalCntBreak">
        <p class="lyMr8 pFont12 pFontGray30">
          <time :datetime="createdAtForAttribute" class="pFontMedium">{{createdAtForHuman}}</time>
        </p>
      </div>
      <div class="lyPd8">
        <template
          v-for="(paragraph, index) in contentParagraph"
        >
          <br v-if="index !== 0">
          {{paragraph}}
        </template>
      </div>
      <div class="lyPl8">
        <div class="lyVerticalBwCnt">
          <div class="lyHorizontal">
            <span v-if="comment.deleted_at !== null || Number(comment.is_self) !== 0">&nbsp;</span>
            <label
              v-else
              class="pChip dHeart dRed"
            >
              <input
                v-model="empathy"
                type="checkbox"
                @change="toggleEmpathy"
              >
              <span class="pFont14 pFontBold">共感<span class="lyMl4 pFontMedium">{{new Intl.NumberFormat().format(empathyCount)}}</span></span>
            </label>
          </div>
          <CommentContainer
            v-if="commentReady && isReply"
            :comment-id="COMMENT_ID"
            :parent-id="comment.id"
            :nickname-id="replyCommentId"
            :height-transition="heightTransition"
            @open-thread="openThread"
          >
            <transition
              name="comment"
              @enter="heightTransition.onEnter"
              @after-enter="heightTransition.onAfter"
              @leave="heightTransition.onLeave"
              @after-leave="heightTransition.onAfter"
            >
              <button
                type="button"
                class="lyPd8 lyPcMrAuto pFont14 pFontBlue pFontBold"
                @click="heightTransition.isOpen.value ? heightTransition.close() : heightTransition.open()"
              >{{heightTransition.isOpen.value ? 'キャンセル' : '返信する'}}</button>
            </transition>
          </CommentContainer>
          <span v-if="comment.deleted_at !== null || Number(composition.state.userId) === 0">&nbsp;</span>
          <button
            v-else-if="Number(comment.is_self) === 0"
            type="button"
            class="lyPd8"
            @click="toggleViolation(true)"
          >
            <img
              :src="siteUrl+(violation ? 'images/iconViolationCrnt.svg' : 'images/iconViolation.svg')"
              style="width:16px"
              width="16"
              height="16"
              loading="lazy"
              alt="違反報告はこちら"
            >
          </button>
          <button
            v-else
            type="button"
            class="lyPd8"
            @click="emits('delete-comment', comment.id)"
          >
            <img
              :src="siteUrl+'images/iconDelete.svg'"
              width="16"
              height="16"
              loading="lazy"
              alt="コメントの削除はこちら"
            >
          </button>
        </div>
      </div>
    </div>
    <div
      :id="COMMENT_ID"
      :class="{pBorderLeft1pxSolidBlack: index === 0, pBorderTop1pxSolidGray4: index === 0, lyMl8: index === 0}"
    ></div>
    <teleport to="body">
      <ConfirmModal
        :is-show="violationModalMode === 1"
        title-text="違反報告"
        submit-text="違反報告を取り消す"
        @request-close="violationModalMode = 0"
        @request-submit="toggleViolation(false)"
      >
        <p class="lyMb16 pFont14">違反報告ありがとうございます。<br>利用規約に照らし合わせて精査いたします。</p>
        <p class="lyMb16 pFont14">※もう一度「違反報告ボタン」をクリックすると取り消しができます。</p>
      </ConfirmModal>
    </teleport>
    <teleport to="body">
      <ConfirmModal
        :is-show="violationModalMode === 2"
        title-text="違反報告取り消し"
        submit-text=""
        @request-close="violationModalMode = 0"
        @request-submit=""
      >
        <p class="lyMb16 pFont14">違反報告は取り消されました。</p>
      </ConfirmModal>
    </teleport>
  </div>
</template>

<style scoped>
.thread-enter-active,
.thread-leave-active,
.comment-enter-active,
.comment-leave-active {
  transition: height 0.5s ease;
  height: auto;
}
</style>
